import { LitElement, html, css } from 'lit';
import { FetchMixin } from '../FetchMixin.js';
import './DashboardLoadingOverlay.js';
import './MessageBox.js';
import './Tooltip.js';
import '../AlertComponent.js';
import { setAlert } from '../AlertSlice.js';
import pwappyPointStore from '../PwappyPointStore.js';
import alertStore from '../AlertStore.js';
import './DashboardAppMenuGroup.js';
import dashboardSelectedAppStore from './DashboardSelectedAppStore.js';
import { selectApp, clearSelectedApp } from './DashboardSelectedAppSlice.js';
import './DashboardVersionList.js';

class DashboardAppDetail extends FetchMixin(LitElement) {

  VersionStatus = {
    DRAFT: { value: 'draft', text: '非公開' },
    PREPARING: { value: 'preparing', text: '公開準備中' },
    REJECTED: { value: 'rejected', text: '公開審査却下' },
    PREPARED: { value: 'prepared', text: '公開準備完了' },
    PUBLISHED: { value: 'published', text: '公開中' },
  };

  ModerationStatus = {
    NOT_CHECKED: { value: 'not_checked', text: '未審査' },
    PREPARING: { value: 'pending', text: '審査中' },
    REJECTED: { value: 'rejected', text: '却下' },
    APPROVED: { value: 'approved', text: '承認' },
    ERROR: { value: 'error', text: 'エラー' },
  };

  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    
    .detail-header {
      display: flex;
      flex-direction: column;
      margin-bottom: 24px;
    }

    .btn-back {
      background: transparent;
      border: none;
      color: #6b7280;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 0;
      margin-bottom: 16px;
      transition: color 0.2s;
    }

    .btn-back:hover {
      color: #111827;
    }

    .app-title-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      padding: 20px 24px;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      border: 1px solid #e5e7eb;
    }

    .app-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .app-name {
      font-size: 1.5rem;
      font-weight: 800;
      color: #111827;
      margin: 0;
    }

    .app-key {
      font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
      font-size: 13px;
      color: #6b7280;
    }

    .detail-tabs {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
      border-bottom: 2px solid #e5e7eb;
    }

    .detail-tab {
      padding: 12px 16px;
      font-weight: 600;
      font-size: 14px;
      color: #6b7280;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -2px;
      transition: all 0.2s;
    }

    .detail-tab.active {
      color: #3b82f6;
      border-bottom-color: #3b82f6;
    }

    .detail-tab:hover:not(.active) {
      color: #111827;
    }

    .section {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      border: 1px solid #e5e7eb;
      padding: 24px;
      margin-bottom: 24px;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .section-header h2 {
      font-size: 1.125rem;
      font-weight: 700;
      color: #111827;
      margin: 0;
    }

    .btn-create-version {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
    }
    .btn-create-version:hover {
      box-shadow: 0 4px 6px rgba(16, 185, 129, 0.3);
      transform: translateY(-1px);
    }

    /* モーダル用 */
    .form-group-item { display: flex; flex-direction: column; margin-bottom: 16px; }
    .form-group-item label { font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 6px; }
    input[type="text"] { padding: 10px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; }
    .error-message { color: #ef4444; font-size: 12px; visibility: hidden; margin-top: 4px; min-height: 16px; }
    .required { color: #ef4444; }

    /* 設定タブのスタイル */
    .settings-container {
      max-width: 768px; 
      width: 100%;
    }

    .setting-list-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 0;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .setting-list-item:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .setting-text {
      flex: 1;
      padding-right: 24px;
    }

    .setting-text strong {
      display: block;
      color: #111827;
      font-size: 15px;
      margin-bottom: 6px;
    }

    .setting-text > span {
      display: block;
      font-size: 13px;
      color: #6b7280;
      line-height: 1.5;
    }

    /* アプリ情報の表示エリア改善 */
    .app-data-display {
      margin-top: 12px;
      padding: 16px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      font-size: 13px;
      display: flex;
      flex-direction: column;
      gap: 16px; /* 項目間の間隔を少し広げて視認性向上 */
    }
    .app-data-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .app-data-label {
      font-size: 11px;
      color: #8b8b8b; /* ラベルを少し控えめな色に */
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .app-data-value {
      font-size: 14px;
      color: #111827;
      font-weight: 600;
    }

    .setting-action {
      flex-shrink: 0;
      min-width: 120px;
      text-align: right;
    }

    /* Danger Zone */
    .danger-zone {
      margin-top: 48px;
      border: 1px solid #fca5a5;
      border-radius: 8px;
      padding: 8px 24px 24px; /* 下側にしっかり余白を持たせる */
      background: #fef2f2;
    }
    
    .danger-zone h3 {
      margin: 0;
      padding: 20px 0 0 0;
      color: #dc2626;
      font-size: 1.1rem;
    }

    .danger-item {
      border-bottom-color: #fecaca;
      padding: 24px 0; /* Danger Zone内の項目にも適切な上下余白を設定 */
    }
    .danger-item:last-child {
      border-bottom: none;
      padding-bottom: 8px; /* 最後の項目の下のパディング */
    }

    /* 各種ボタン */
    .readonly { background-color: #f3f4f6; color: #6b7280; cursor: not-allowed; }

    .btn-archive-action {
      background: white; color: #d97706; border: 1px solid #d97706;
      padding: 8px 16px; border-radius: 6px; font-weight: 600; font-size: 13px; cursor: pointer;
      transition: background-color 0.2s; white-space: nowrap;
    }
    .btn-archive-action:hover { background: #fef3c7; }
    
    .btn-danger {
      background: white; color: #dc2626; border: 1px solid #dc2626;
      padding: 8px 16px; border-radius: 6px; font-weight: 600; font-size: 13px; cursor: pointer;
      transition: background-color 0.2s; white-space: nowrap;
    }
    .btn-danger:hover:not(:disabled) { background: #fee2e2; }
    .btn-danger:disabled {
      background-color: #f3f4f6; color: #9ca3af; border-color: #d1d5db; cursor: not-allowed;
    }
    
    .btn-warning {
      background: white; color: #374151; border: 1px solid #d1d5db;
      padding: 8px 16px; border-radius: 6px; font-weight: 600; font-size: 13px; cursor: pointer;
      transition: background-color 0.2s; white-space: nowrap;
    }
    .btn-warning:hover { background: #f3f4f6; }

    .confirm-ok-button { background-color: #ef4444; color: white; border: none; padding: 10px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; }
    .confirm-cancel-button { background-color: #f3f4f6; color: #374151; border: none; padding: 10px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; }
    #prepare-publish-confirm .confirm-ok-button { background-color: #3b82f6; }
    
    .caution { font-size: 40px; display: flex; margin: 0 0 16px 0; justify-content: center; color: #f59e0b; }
    .question { font-size: 40px; display: flex; margin: 0 0 16px 0; justify-content: center; color: #9ca3af; }
    .rejection-detail-message { white-space: pre-wrap; text-align: left; font-size: 0.9em; height: 90%; overflow-y: auto; border: 1px solid #e5e7eb; padding: 16px; border-radius: 8px; background-color: #f9fafb; color: #374151; }

    @media (max-width: 640px) {
      .setting-list-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
      }
      .setting-text {
        padding-right: 0;
      }
      .setting-action {
        width: 100%;
      }
      .setting-action button {
        width: 100%;
      }
    }
  `;

  static get properties() {
    return {
      identKey: { type: String },
      app: { type: Object },
      versions: { type: Array },
      loading: { type: Boolean },
      activeTab: { type: String },
      versionDialogMode: { type: String },
      autoRefreshIntervalId: { state: true }
    };
  }

  constructor() {
    super();
    this.identKey = null;
    this.app = null;
    this.versions = [];
    this.loading = true;
    this.activeTab = 'versions'; // 'versions' or 'settings'
    this.versionDialogMode = null;
    this.autoRefreshIntervalId = null;
    this.currentRefreshDelay = 5000;

    this.addEventListener('modal-submit', this.handleModalSubmit.bind(this));
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchAppDetailData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAutoRefresh();
  }

  firstUpdated() {
    const fontAwasomeStyle = document.querySelector("link[rel='stylesheet'][data-name='fontawesomev6']");
    if (fontAwasomeStyle) {
      const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
      fontAwasomeStyleClone.removeAttribute('disabled');
      this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);
    }
  }

  async fetchAppDetailData() {
    this.loading = true;
    const selectedAppKey = dashboardSelectedAppStore.getState().dashboardSelectedApp.selectedAppKey;
    if (!selectedAppKey) return;

    try {
      const json = await this.fetchWithAuth(process.env.PUBLISHE_LIST_ENDPOINT, { method: 'GET' });
      if (json.code === 200 && json.details) {
        if (json.details.identKey) {
          this.identKey = json.details.identKey;
          this.dispatchEvent(new CustomEvent('changeIdentKey', { detail: { identKey: this.identKey }, bubbles: true, composed: true }));
        }

        const apps = json.details.applications || [];
        this.app = apps.find(a => a.appKey === selectedAppKey);
        this.versions = (json.details.versions || [])
          .filter(v => v.appKey === selectedAppKey)
          .map(v => ({ ...v, fileModerationResults: typeof v.fileModerationResults === 'string' ? JSON.parse(v.fileModerationResults) : v.fileModerationResults }));
      }
    } catch (error) {
      console.error('Error fetching app details:', error);
      alertStore.dispatch(setAlert('アプリ情報の取得中にエラーが発生しました。'));
    } finally {
      this.loading = false;
      this.startAutoRefresh();
    }
  }

  goBack() {
    dashboardSelectedAppStore.dispatch(clearSelectedApp());
  }

  changeTab(tabName) {
    this.activeTab = tabName;
  }

  // --- バージョン管理関連 ---

  async addNewVersionDialog() {
    this.clearVersionDialog();
    this.versionDialogMode = 'add';
    let versionModal = this.shadowRoot.querySelector('#versionModal');
    await versionModal.show();
    this.shadowRoot.getElementById('input-version').focus();
  }

  clearVersionDialog() {
    let inputs = this.shadowRoot.querySelectorAll('#versionModal .modal-input');
    inputs.forEach(input => input.value = '');
    let errorMessages = this.shadowRoot.querySelectorAll('#versionModal .error-message');
    errorMessages.forEach(message => message.style.visibility = 'hidden');
    let inputVersion = this.shadowRoot.querySelector('#input-version')
    if (inputVersion) { inputVersion.readOnly = false; inputVersion.classList.remove('readonly'); }
  }

  async editVersion(versionString) {
    this.clearVersionDialog();
    this.versionDialogMode = 'edit';
    let versionObj = this.versions.find(v => v.version === versionString);
    if (versionObj) {
      this.shadowRoot.querySelector('#input-version').value = versionObj.version;
      let inputVersion = this.shadowRoot.querySelector('#input-version')
      if (versionObj.status !== this.VersionStatus.DRAFT.value) {
        inputVersion.readOnly = true;
        inputVersion.classList.add('readonly');
        let errVersion = this.shadowRoot.getElementById('error-version');
        errVersion.textContent = `公開準備または公開のバージョンがある場合はこの項目を編集できません`;
        errVersion.style.visibility = 'visible';
      }
    }
    let versionModal = this.shadowRoot.querySelector('#versionModal');
    versionModal.dataset.originVersion = versionString;
    await versionModal.show();
    this.shadowRoot.getElementById('input-version').focus();
  }

  async duplicateVersion(versionString) {
    let options = { method: 'POST', body: JSON.stringify({ appKey: this.app.appKey, version: versionString }) }
    this.loading = true;
    try {
      let ret = await this.fetchWithAuth(process.env.VERSION_DUPLICATE_ENDPOINT, options);
      if (ret.code !== 200) {
        if (ret.status === "app_version_duplicate_disk_space_over") alertStore.dispatch(setAlert("割り当てられたスペースを超えているので複製できませんでした"));
        else alertStore.dispatch(setAlert('エラーが発生しました'));
        throw new Error(JSON.stringify(ret));
      }
      await this.fetchAppDetailData();
    } catch (e) { console.error(e); } finally { this.loading = false; }
  }

  async showDeleteVersionConfirm(versionString) {
    const messageBox = this.shadowRoot.querySelector('#delete-confirm-general');
    if (this.loading || messageBox.visible) return;
    messageBox.querySelector('#delete-confirm-msg').textContent = `バージョン ${versionString} を完全に削除しますか？`;
    const ret = await messageBox.show();
    if (ret) {
      let options = { method: 'DELETE', body: JSON.stringify({ appKey: this.app.appKey, version: versionString }) }
      this.loading = true;
      try {
        await this.fetchWithAuth(process.env.VERSION_ENDPOINT, options);
        await this.fetchAppDetailData();
      } catch (error) { console.error('Error:', error) }
      finally { this.loading = false; }
    }
    messageBox.hide();
  }

  async handleModalSubmit(event) {
    if (event.detail.dialogName === 'versionModal') {
      let versionModal = this.shadowRoot.querySelector('#versionModal');
      let inputVersion = this.shadowRoot.getElementById('input-version');
      let errVersion = this.shadowRoot.getElementById('error-version');
      errVersion.style.visibility = 'hidden';

      let errorMessages = [];
      if (!inputVersion.checkValidity()) {
        if (inputVersion.validity.valueMissing) errorMessages.push(`必須項目です`);
        if (inputVersion.validity.patternMismatch) errorMessages.push(`英小文字、数字、ハイフン、アンダーバー、ドットのみ入力可能です`);
        if (inputVersion.validity.tooLong) errorMessages.push(`最大文字数を超えています`);
        errVersion.textContent = errorMessages.join(' '); errVersion.style.visibility = 'visible';
        return;
      }

      const selectedAppKey = this.app.appKey;
      this.loading = true;
      try {
        if (this.versionDialogMode === 'add') {
          let options = { method: 'POST', body: JSON.stringify({ appKey: selectedAppKey, version: inputVersion.value }) };
          let ret = await this.fetchWithAuth(process.env.VERSION_ENDPOINT, options);
          if (ret.code !== 200) {
            if (ret.status === "app_version_create_duplicate_key_error") { errVersion.textContent = `バージョンが重複しています`; errVersion.style.visibility = 'visible'; return; }
            else if (ret.status === "app_version_create_user_space_error") alertStore.dispatch(setAlert("割り当てられたスペースを超えているので作成できませんでした"));
            else alertStore.dispatch(setAlert("エラーが発生しました"));
            throw new Error(JSON.stringify(ret));
          }
        } else if (this.versionDialogMode === 'edit') {
          let options = { method: 'PUT', body: JSON.stringify({ appKey: selectedAppKey, originVersion: versionModal.dataset.originVersion, newVersion: inputVersion.value }) };
          let ret = await this.fetchWithAuth(process.env.VERSION_ENDPOINT, options);
          if (ret.code !== 200 && ret.status === "app_version_update_key_duplicate_error") { errVersion.textContent = `バージョンが重複しています`; errVersion.style.visibility = 'visible'; return; }
        }
        versionModal.hide();
        await this.fetchAppDetailData();
      } catch (e) { console.error(e); } finally { this.loading = false; }

    } else if (event.detail.dialogName === 'appEditModal') {
      let inputAppName = this.shadowRoot.getElementById('edit-app-name');
      let errAppName = this.shadowRoot.getElementById('error-edit-app-name');
      errAppName.style.visibility = 'hidden';

      let inputAppKey = this.shadowRoot.getElementById('edit-app-key');
      let errAppKey = this.shadowRoot.getElementById('error-edit-app-key');
      errAppKey.style.visibility = 'hidden';

      let hasError = false;

      if (!inputAppName.checkValidity()) {
        let keyMsgs = [];
        if (inputAppName.validity.valueMissing) keyMsgs.push(`必須項目です`);
        if (inputAppName.validity.tooLong) keyMsgs.push(`最大文字数を超えています`);
        errAppName.textContent = keyMsgs.join(' ');
        errAppName.style.visibility = 'visible';
        hasError = true;
      }

      if (!inputAppKey.checkValidity()) {
        let keyMsgs = [];
        if (inputAppKey.validity.valueMissing) keyMsgs.push(`必須項目です`);
        if (inputAppKey.validity.patternMismatch) keyMsgs.push(`英小文字、数字、ハイフン、アンダーバーのみ入力可能です`);
        if (inputAppKey.validity.tooLong) keyMsgs.push(`最大文字数を超えています`);
        errAppKey.textContent = keyMsgs.join(' ');
        errAppKey.style.visibility = 'visible';
        hasError = true;
      }

      if (hasError) return;

      this.loading = true;
      try {
        let options = { method: 'PUT', body: JSON.stringify({ appName: inputAppName.value, originAppKey: this.app.appKey, newAppKey: inputAppKey.value }) };
        let ret = await this.fetchWithAuth(process.env.APPLICATION_ENDPOINT, options);

        if (ret.code !== 200) {
          if (ret.status === "app_update_key_duplicate_error") {
            errAppKey.textContent = `アプリケーションキーが重複しています`;
            errAppKey.style.visibility = 'visible';
          } else {
            alertStore.dispatch(setAlert("更新中にエラーが発生しました"));
          }
          return;
        }

        this.shadowRoot.querySelector('#appEditModal').hide();
        if (this.app.appKey !== inputAppKey.value) {
          dashboardSelectedAppStore.dispatch(selectApp(inputAppKey.value));
        }
        await this.fetchAppDetailData();
      } catch (e) { console.error(e); } finally { this.loading = false; }
    }
  }

  async fetchVersionModerationStatusDetail(versionString) {
    const messageBox = this.shadowRoot.querySelector('#rejection-reason-detail');
    if (this.loading || messageBox.visible) return;
    this.loading = true;
    try {
      const endpoint = `${process.env.MODERATION_STATUS_ENDPOINT}?appKey=${this.app.appKey}&version=${versionString}`;
      const json = await this.fetchWithAuth(endpoint, { method: 'GET' });
      if (json.code === 200 && json.details) {
        const fileModerationResults = typeof json.details.fileModerationResults === 'string' ? JSON.parse(json.details.fileModerationResults) : json.details.fileModerationResults;
        this.showRejectionReasonPopup(fileModerationResults, this.app.appKey, versionString);
      } else { alertStore.dispatch(setAlert(json.message || '却下理由の詳細取得に失敗しました。')); }
    } catch (error) { console.error(error); alertStore.dispatch(setAlert('却下理由の詳細取得中にエラーが発生しました。')); }
    finally { this.loading = false; }
  }

  showRejectionReasonPopup(fileModerationResults, appKey, versionString) {
    const messageBox = this.shadowRoot.querySelector('#rejection-reason-detail');
    const messageSlot = messageBox.querySelector('#rejection-reason-message');

    let reasonsHtml = '';
    const rejectedFiles = fileModerationResults.filter(f => f.status.ModerationStatus === this.ModerationStatus.REJECTED.value);
    const errorFiles = fileModerationResults.filter(f => f.status.ModerationStatus === this.ModerationStatus.ERROR.value);

    if (rejectedFiles.length > 0) {
      reasonsHtml = rejectedFiles.map(f => {
        const fullPath = f.filePath.FilePath || '不明なファイルパス';
        const appSpecificPathSegment = `/${appKey}/${versionString}/`;
        const startIndex = fullPath.indexOf(appSpecificPathSegment);
        let simplifiedPath = startIndex !== -1 ? fullPath.substring(startIndex + appSpecificPathSegment.length - 1) : fullPath.substring(fullPath.lastIndexOf('/') + 1);
        return `<div><strong>${simplifiedPath}</strong>: ${f.reason.Reason || '理由なし'}</div>`;
      }).join('');
    } else if (errorFiles.length > 0) {
      reasonsHtml = errorFiles.map(f => {
        const errorMessage = f.errorMessage?.ErrorMessage || '理由なし';
        return `<div><strong>エラー</strong>: ${errorMessage}</div>`;
      }).join('');
    } else { reasonsHtml = '<div>却下されたファイルはありませんが、バージョンは却下されています。</div>'; }

    messageSlot.innerHTML = `<div style="text-align: center; margin-bottom: 15px; font-size: 1.1em;"><strong>却下理由詳細</strong></div>${reasonsHtml}`;
    messageBox.show();
  }

  async changeVersionStatus(versionString, newStatus, requiresPP = false) {
    const messageBox = this.shadowRoot.querySelector('#publish-action-confirm');
    let message = ''; let confirmButtonText = '続ける';

    switch (newStatus) {
      case this.VersionStatus.PREPARING.value:
        message = requiresPP ? `バージョン ${versionString} を審査に提出しますか？\n\n※現在は無料で審査できます` : `バージョン ${versionString} を公開準備状態に戻しますか？`;
        confirmButtonText = requiresPP ? '申請する' : '戻す';
        break;
      case this.VersionStatus.PUBLISHED.value: message = `バージョン ${versionString} を本番環境へ公開しますか？`; confirmButtonText = '公開する'; break;
      case this.VersionStatus.DRAFT.value: message = `バージョン ${versionString} を非公開にしますか？`; confirmButtonText = '非公開にする'; break;
      case this.VersionStatus.PREPARED.value: message = `バージョン ${versionString} の公開を停止し、準備状態に戻しますか？`; confirmButtonText = '公開停止'; break;
    }

    messageBox.querySelector('#publish-confirm-message').textContent = message;
    messageBox.querySelector('#publish-confirm-ok-button').textContent = confirmButtonText;

    const confirmed = await messageBox.show();
    if (confirmed) {
      this.loading = true;
      try {
        const options = { method: 'PUT', body: JSON.stringify({ appKey: this.app.appKey, version: versionString, status: newStatus }) };
        const ret = await this.fetchWithAuth(process.env.VERSION_STATUS_ENDPOINT, options);
        if (ret.code === 200) this.currentRefreshDelay = 5000;
        await this.fetchAppDetailData();
        this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
      } catch (error) { console.error(error); }
      finally { this.loading = false; messageBox.hide(); }
    } else { messageBox.hide(); }
  }

  async handleDownload(versionString) {
    const messageBox = this.shadowRoot.querySelector('#download-confirm');
    const confirmed = await messageBox.show();

    if (confirmed) {
      const pwappyPoints = pwappyPointStore.getState().pwappyPoint.pwappyPoints;
      if (pwappyPoints < 10) { alertStore.dispatch(setAlert("ダウンロードには１０ＰＰが必要です")); messageBox.hide(); return; }

      this.loading = true;
      try {
        const options = { method: 'POST', body: JSON.stringify({ appKey: this.app.appKey, version: versionString }) };
        const response = await this.fetchWithAuthDownload(process.env.DOWNLOAD_OUTPUT_ZIP_ENDPOINT, options);
        if (response.status !== 200) throw new Error('Download failed');

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const json = await response.json();
          if (json.status === 'pwappy_point_not_enough') alertStore.dispatch(setAlert(json.message));
        } else {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none'; a.href = url;
          const now = new Date();
          const formattedTime = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
          const safeAppKey = this.app.appKey.replace(/\./g, '_');
          const safeVersion = versionString.replace(/\./g, '_');
          const fileName = `${safeAppKey}_${safeVersion}_${formattedTime}.zip`;

          a.download = fileName;
          document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
        }
        this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
      } catch (error) { console.error(error); }
      finally { this.loading = false; messageBox.hide(); }
    } else { messageBox.hide(); }
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    if (this.versions.some(v => v.status === this.VersionStatus.PREPARING.value)) {
      this.autoRefreshIntervalId = setTimeout(async () => {
        await this.fetchAppDetailData();
        this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
        this.currentRefreshDelay = Math.min(60000, this.currentRefreshDelay * 2);
        this.startAutoRefresh();
      }, this.currentRefreshDelay);
    } else { this.currentRefreshDelay = 5000; }
  }

  stopAutoRefresh() {
    if (this.autoRefreshIntervalId) { clearTimeout(this.autoRefreshIntervalId); this.autoRefreshIntervalId = null; }
  }

  // --- 設定タブの機能 ---
  async showAppEditDialog() {
    const modal = this.shadowRoot.querySelector('#appEditModal');

    this.shadowRoot.querySelector('#edit-app-name').value = this.app.appName;
    this.shadowRoot.querySelector('#error-edit-app-name').style.visibility = 'hidden';

    let inputAppKey = this.shadowRoot.querySelector('#edit-app-key');
    let errAppKey = this.shadowRoot.querySelector('#error-edit-app-key');
    inputAppKey.value = this.app.appKey;
    errAppKey.style.visibility = 'hidden';

    if (this.versions.some(version => version.status !== this.VersionStatus.DRAFT.value)) {
      inputAppKey.readOnly = true;
      inputAppKey.classList.add('readonly');
      errAppKey.textContent = `公開準備または公開中のバージョンがある場合はアプリキーを編集できません`;
      errAppKey.style.visibility = 'visible';
    } else {
      inputAppKey.readOnly = false;
      inputAppKey.classList.remove('readonly');
    }

    await modal.show();
    this.shadowRoot.querySelector('#edit-app-name').focus();
  }

  async archiveApp() {
    const messageBox = this.shadowRoot.querySelector('#archive-confirm');
    const ret = await messageBox.show();
    if (ret) {
      this.loading = true;
      try {
        await this.fetchWithAuth(process.env.APPLICATION_ARCHIVE_ENDPOINT, { method: 'POST', body: JSON.stringify({ appKey: this.app.appKey }) });
        alertStore.dispatch(setAlert(`アプリ「${this.app.appName}」をアーカイブしました`));
        this.goBack();
        this.dispatchEvent(new CustomEvent('updatePwappyPoint', { bubbles: true, composed: true }));
      } catch (e) { console.error(e); } finally { this.loading = false; messageBox.hide(); }
    } else { messageBox.hide(); }
  }

  async deleteApp() {
    // 公開中バージョンがある場合は削除不可にするガード
    const hasPublishedVersion = this.versions.some(v => v.status === this.VersionStatus.PUBLISHED.value);
    if (hasPublishedVersion) {
      alertStore.dispatch(setAlert('公開中のバージョンが存在するアプリケーションは削除できません。'));
      return;
    }

    const messageBox = this.shadowRoot.querySelector('#delete-confirm-general');
    messageBox.querySelector('#delete-confirm-msg').textContent = `アプリを完全に削除しますか？\n（元に戻すことはできません）`;
    const ret = await messageBox.show();
    if (ret) {
      this.loading = true;
      try {
        await this.fetchWithAuth(process.env.APPLICATION_ENDPOINT, { method: 'DELETE', body: JSON.stringify({ appKey: this.app.appKey }) });
        this.goBack();
      } catch (e) { console.error(e); } finally { this.loading = false; messageBox.hide(); }
    } else { messageBox.hide(); }
  }


  _handleVersionAction(e) {
    const { action, appKey, version } = e.detail;
    if (action === 'editor') window.open('/app_editor.html?identKey=' + this.identKey + '&appKey=' + appKey + '&version=' + version, '_blank');
    else if (action === 'edit') this.editVersion(version);
    else if (action === 'duplicate') this.duplicateVersion(version);
    else if (action === 'delete') this.showDeleteVersionConfirm(version);
    else if (action === 'reason') this.fetchVersionModerationStatusDetail(version);
    else if (action === 'download') this.handleDownload(version);
    else if (action === 'status_preparing') this.changeVersionStatus(version, this.VersionStatus.PREPARING.value, true);
    else if (action === 'status_draft') this.changeVersionStatus(version, this.VersionStatus.DRAFT.value);
    else if (action === 'status_published') this.changeVersionStatus(version, this.VersionStatus.PUBLISHED.value);
    else if (action === 'status_prepared') this.changeVersionStatus(version, this.VersionStatus.PREPARED.value, false);
  }


  /**
   * バージョン入力欄でのEnterキー判定
   */
  handleVersionKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      // handleModalSubmit を直接呼び出して保存処理を実行
      this.handleModalSubmit(new CustomEvent('modal-submit', {
        detail: { dialogName: 'versionModal' }
      }));
    }
  }

  /**
   * アプリケーション編集欄でのEnterキー判定
   */
  handleAppEditKeyDown(event) {
    if (event.key === 'Enter') {
      if (event.target.id === 'edit-app-name') {
        // 名前からキーへフォーカス移動
        this.shadowRoot.getElementById('edit-app-key').focus();
      } else if (event.target.id === 'edit-app-key') {
        event.preventDefault();
        // 保存処理を実行
        this.handleModalSubmit(new CustomEvent('modal-submit', {
          detail: { dialogName: 'appEditModal' }
        }));
      }
    }
  }

  // ... (既存のメソッド群: fetchAppDetailData, handleModalSubmit などはそのまま)

  render() {
    if (!this.app && !this.loading) return html`<div style="text-align:center; padding: 40px;">データが見つかりません</div>`;

    // 公開中のバージョンがあるか判定
    const hasPublishedVersion = this.versions.some(v => v.status === this.VersionStatus.PUBLISHED.value);

    return html`
      ${this.loading ? html`<dashboard-loading-overlay></dashboard-loading-overlay>` : ''}
      
      <!-- バージョン追加・編集モーダル -->
      <dashboard-modal-window id="versionModal">
        <span slot="header-title">${this.versionDialogMode === 'add' ? 'バージョンの追加' : 'バージョンの編集'}</span>
        <div slot="content">
          <div class="form-group-item">
            <label for="input-version">バージョン名<span class="required">*</span></label>
            <input type="text" id="input-version" class="modal-input" required maxlength="30" 
              pattern="^[a-z0-9\\.\\-_]+$" placeholder="例: 1.0.0"
              @keydown="${this.handleVersionKeyDown}">
            <div class="error-message" id="error-version"></div>
          </div>
        </div>
        <span slot="submit-button-text">保存</span>
      </dashboard-modal-window>

      <!-- アプリケーション編集モーダル -->
      <dashboard-modal-window id="appEditModal">
        <span slot="header-title">アプリケーションの編集</span>
        <div slot="content">
          <div class="form-group-item">
            <label for="edit-app-name">アプリケーション名<span class="required">*</span></label>
            <input type="text" id="edit-app-name" class="modal-input" required maxlength="30"
              @keydown="${this.handleAppEditKeyDown}">
            <div class="error-message" id="error-edit-app-name"></div>
          </div>
          <div class="form-group-item">
            <label for="edit-app-key">アプリケーションキー<span class="required">*</span></label>
            <input type="text" id="edit-app-key" class="modal-input" required maxlength="30" 
              pattern="^[a-z0-9\\-_]+$" @keydown="${this.handleAppEditKeyDown}">
            <div class="error-message" id="error-edit-app-key"></div>
          </div>
        </div>
        <span slot="submit-button-text">保存</span>
      </dashboard-modal-window>

      <message-box id="publish-action-confirm">
        <p slot="message-slot" id="publish-confirm-message" style="text-align:center; font-weight:bold; color:#374151; white-space:pre-wrap;"></p>
        <button slot="ok-slot" id="publish-confirm-ok-button" class="confirm-ok-button">OK</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <message-box id="delete-confirm-general">
        <div slot="message-slot">
            <p class="caution"><i class="fa-solid fa-triangle-exclamation"></i></p>
            <p id="delete-confirm-msg" style="text-align: center; font-weight: bold; white-space:pre-wrap;"></p>
        </div>
        <button slot="ok-slot" class="confirm-ok-button">削除する</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <message-box id="archive-confirm">
        <div slot="message-slot">
            <p class="question"><i class="fa-solid fa-circle-question"></i></p>
            <p style="text-align: center; font-weight: bold;">アーカイブに退避しますか？<br><span style="font-size:0.8em; color:#6b7280; font-weight:normal;">※１ＰＰ使用します</span></p>
        </div>
        <button slot="ok-slot" class="confirm-ok-button" style="background:#d97706;">アーカイブ</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <message-box id="download-confirm">
        <div slot="message-slot">
            <p class="question"><i class="fa-solid fa-circle-question"></i></p>
            <p style="text-align: center; font-weight:bold;">公開用のファイルをダウンロードしますか？<br><span style="font-size:0.85em; font-weight:normal; color:#6b7280;">※10PPを使用します</span></p>
        </div>
        <button slot="ok-slot" class="confirm-ok-button" style="background:#10b981;">ダウンロード</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <message-box id="rejection-reason-detail" >
        <div slot="message-slot" id="rejection-reason-message" class="rejection-detail-message"></div>
        <button slot="ok-slot" class="confirm-cancel-button">閉じる</button>
      </message-box>

      <div class="detail-header">
        <button class="btn-back" @click=${this.goBack}><i class="fa-solid fa-arrow-left"></i> ワークベンチに戻る</button>
        
        <div class="app-title-bar">
          <div class="app-info">
            <h1 class="app-name">${this.app ? this.app.appName : ''}</h1>
            <span class="app-key">${this.app ? this.app.appKey : ''}</span>
          </div>
          ${this.app && this.versions.some(v => v.status === this.VersionStatus.PUBLISHED.value) ? html`
            <dashboard-app-menu-group identKey="${this.identKey}" appKey="${this.app.appKey}"></dashboard-app-menu-group>
          ` : ''}
        </div>
      </div>

      <div class="detail-tabs">
        <div class="detail-tab ${this.activeTab === 'versions' ? 'active' : ''}" @click=${() => this.changeTab('versions')}>バージョン管理</div>
        <div class="detail-tab ${this.activeTab === 'settings' ? 'active' : ''}" @click=${() => this.changeTab('settings')}>アプリ設定</div>
      </div>

      ${this.activeTab === 'versions' ? html`
        <div class="section">
          <div class="section-header">
            <h2>バージョン一覧</h2>
            <button class="btn-create-version" @click=${this.addNewVersionDialog}><i class="fa-solid fa-plus"></i> 新規バージョン</button>
          </div>
          <dashboard-version-list
            mode="detail"
            .versions=${this.versions}
            .selectedApp=${this.app}
            .identKey=${this.identKey}
            .envDevUrlBase=${process.env.DEV_URL_BASE}
            .isDev=${process.env.NODE_ENV === 'development'}
            @version-action=${this._handleVersionAction}
            @trigger-create-version=${this.addNewVersionDialog}
          ></dashboard-version-list>
        </div>
      ` : html`
        <div class="section">
          <div class="section-header"><h2>一般設定</h2></div>
          <div class="settings-container">
            <div class="settings-row">
               
               <!-- アプリケーション情報 -->
               <div class="setting-list-item">
                 <div class="setting-text">
                   <strong>アプリケーション情報</strong>
                   <span>アプリケーションの名称とURLなどに使用される一意のキー（ID）です。</span>
                   <div class="app-data-display">
                     <div class="app-data-item">
                       <span class="app-data-label">アプリケーション名</span>
                       <span class="app-data-value">${this.app.appName}</span>
                     </div>
                     <div class="app-data-item">
                       <span class="app-data-label">アプリケーションキー</span>
                       <span class="app-data-value" style="font-family:monospace;">${this.app.appKey}</span>
                     </div>
                   </div>
                 </div>
                 <div class="setting-action">
                   <button class="btn-warning" @click=${this.showAppEditDialog}>編集する</button>
                 </div>
               </div>
  
               <!-- アーカイブ（退避） -->
               <div class="setting-list-item">
                 <div class="setting-text">
                   <strong>アーカイブ（退避）</strong>
                   <span>作業を一時中断する場合、ワークベンチからアーカイブへ退避させることで維持費（PP）を節約できます。<br>再開する際はアーカイブ一覧からいつでも復元可能です。（アーカイブ時 1PP消費）</span>
                 </div>
                 <div class="setting-action">
                   <button class="btn-archive-action" @click=${this.archiveApp}>
                     <i class="fa-solid fa-box-archive"></i> アーカイブする
                   </button>
                 </div>
               </div>
  
            </div>
  
            <div class="danger-zone">
              <h3>Danger Zone (危険な操作)</h3>
              <div class="setting-list-item danger-item">
                <div class="setting-text danger-text">
                  <strong>アプリケーションの完全削除</strong>
                  <span>すべてのバージョンとファイルが削除され、復元できなくなります。</span>
                  ${hasPublishedVersion ? html`<span style="color:#ef4444; margin-top:8px; font-weight:bold; display:block;">※公開中のバージョンが存在するため削除できません。</span>` : ''}
                </div>
                <div class="setting-action">
                  <button class="btn-danger" @click=${this.deleteApp} ?disabled=${hasPublishedVersion}>削除する</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `}
    `;
  }
}

customElements.define('dashboard-app-detail', DashboardAppDetail);